import { useCallback, useEffect } from 'react';
import {
  type EventData,
  type EventOptions,
  type EventPayload,
  type EventType,
} from './defn';

type EventHandler<T = EventPayload> = (payload: T) => void;

const useMessage = <T = EventPayload>(
  eventType: EventType,
  eventHandler: EventHandler<T>,
  options: EventOptions
) => {
  const onWatchEventHandler = useCallback(
    ({ origin, data }: MessageEvent) => {
      // Check if data exists and has the expected structure
      if (!data || typeof data !== 'object') {
        return;
      }

      // Type guard for EventData
      const isEventData = (d: unknown): d is EventData =>
        typeof d === 'object' &&
        d !== null &&
        'source' in d &&
        (d as { source: unknown }).source === 'pricify' &&
        'type' in d &&
        'payload' in d;

      // Check if this is a pricify message
      if (isEventData(data)) {
        const { type, payload } = data;
        // Check if this is the event type we're listening for
        if (type !== eventType) {
          return;
        }

        // Validate recipient if pricingPageKey is provided in options
        if (
          options.pricingPageKey &&
          payload &&
          typeof payload === 'object' &&
          'pricingPageKey' in payload &&
          (payload as { pricingPageKey?: string }).pricingPageKey !==
            options.pricingPageKey &&
          (payload as { pricingPageKey?: string }).pricingPageKey !==
            options.sessionId
        ) {
          console.log('[useMessage] Incorrect recipient:', payload);
          return;
        }

        eventHandler(payload as T);
        return;
      }

      // Handle Chargebee events
      if (origin.match(/https:\/\/[A-Za-z0-9\-_.]+chargebee.com/g)) {
        if (
          typeof data === 'object' &&
          data !== null &&
          'key' in data &&
          eventType === (data as { key: unknown }).key
        ) {
          eventHandler(data as T);
        }
        return;
      }

      // Check if origin matches the expected host (for non-pricify messages)
      if (
        isEventData(data) &&
        origin === options.host &&
        data.type === eventType
      ) {
        eventHandler(data.payload as T);
        return;
      }

      // Same origin - allow (for non-pricify messages)
      if (
        isEventData(data) &&
        origin === window.location.origin &&
        data.type === eventType
      ) {
        eventHandler(data.payload as T);
        return;
      }

      // Log unhandled messages for debugging
      if (isEventData(data) && data.type === eventType) {
        console.log('[useMessage] Message not handled:', {
          origin,
          expectedHost: options.host,
          type: data.type,
          source: data.source,
        });
      }
    },
    [eventType, eventHandler, options]
  );

  useEffect(() => {
    window.addEventListener('message', onWatchEventHandler);
    return () => window.removeEventListener('message', onWatchEventHandler);
  }, [eventType, onWatchEventHandler]);

  return {};
};

export default useMessage;
